// Single-facet filter trigger that opens a popover (desktop) or drawer (touch).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { useSystemDeviceDetection } from '../../hooks';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../ui/drawer';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '@/utilities/cn';

export type FilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type FilterGroupProps = {
  label: string;
  options: FilterOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  searchPlaceholder?: string;
  searchable?: boolean;
};

const FilterGroup: React.FC<FilterGroupProps> = ({
  label,
  options,
  selectedValues,
  onToggle,
  searchPlaceholder,
  searchable = true,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const resultsViewportRef = useRef<HTMLDivElement | null>(null);
  const { deviceType } = useSystemDeviceDetection();
  const useDrawer = deviceType === 'mobile' || deviceType === 'tablet';
  const selectionSignature = useMemo(
    () => JSON.stringify(selectedValues),
    [selectedValues]
  );
  const previousSelectionSignatureRef = useRef(selectionSignature);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      requestAnimationFrame(() => {
        resultsViewportRef.current?.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto',
        });
      });
    }
    if (!nextOpen) {
      setSearch('');
    }
  };

  useEffect(() => {
    if (
      open &&
      useDrawer &&
      previousSelectionSignatureRef.current !== selectionSignature
    ) {
      requestAnimationFrame(() => {
        resultsViewportRef.current?.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto',
        });
      });
    }
    previousSelectionSignatureRef.current = selectionSignature;
  }, [open, selectionSignature, useDrawer]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }
    const query = search.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, search]);

  const trigger = (
    <Button variant="outline" size="sm" type="button">
      {label}
      {selectedValues.length > 0 && (
        <Badge variant="secondary">{selectedValues.length}</Badge>
      )}
      <ChevronDownIcon data-icon="inline-end" />
    </Button>
  );

  const content = (
    <div className={cn('flex flex-col gap-3', useDrawer && 'min-h-0 flex-1')}>
      {searchable && (
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder || `Search ${label.toLowerCase()}`}
            className="pl-8"
          />
        </div>
      )}
      <div
        ref={resultsViewportRef}
        className={cn(
          'overflow-y-auto overscroll-contain',
          useDrawer ? 'min-h-24 flex-1 pr-1 pb-1' : 'max-h-64'
        )}
      >
        <div className="flex flex-col gap-1">
          {filteredOptions.map((option) => {
            const selected = selectedSet.has(option.value);
            return (
              <button
                key={option.value}
                type="button"
                disabled={option.disabled}
                className={cn(
                  'flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  selected
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-muted',
                  option.disabled && 'cursor-not-allowed opacity-50'
                )}
                onClick={() => onToggle(option.value)}
              >
                <span className="truncate">{option.label}</span>
                {selected && <CheckIcon />}
              </button>
            );
          })}
          {filteredOptions.length === 0 && (
            <span className="px-2 py-1.5 text-sm text-muted-foreground">
              No options found.
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (useDrawer) {
    return (
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        direction="bottom"
        fixed
        autoFocus={false}
        shouldScaleBackground={false}
      >
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[88svh] overflow-hidden rounded-t-2xl">
          <DrawerHeader className="px-4 pt-5 pb-2 text-left">
            <DrawerTitle>Filter by {label}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {searchable
                ? 'Search available options and tap an item to toggle the filter.'
                : 'Tap an item to toggle the filter.'}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex min-h-0 flex-1 flex-col px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        {content}
      </PopoverContent>
    </Popover>
  );
};

export default FilterGroup;
